'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import { parse } from 'qs';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';

import {FaKey, FaFileAlt} from "react-icons/fa";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Tabs from 'react-bootstrap/Tabs';
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import {InstitutionCodes} from '@uw-it-sis/lib-react-myplan/lib/SectionConstants';
import HelpPopover from '@uw-it-sis/lib-react/lib/HelpPopover';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {parseJson} from '@uw-it-sis/lib-react/lib/JsonParseUtils';

import SectionsTable from './SectionsTable';

/**
* Groups sections by term.
*/

const SectionsTermPanel = (props) => {
    let help =
       <HelpPopover title="Course Sections Help" placement="left" size="1.5rem">
           <p><strong>Est ENRL Status</strong> = section enrollment and overall enrollment limit. Click a section's SLN for up-to-date seat availability status.</p>
           <p><strong>Additional Details</strong> = additional information for each section. Expand the section to see additional details.</p>
           <p><FaKey className="lead text-muted" aria-hidden={true} /> = section requires entry code</p>
           <p><FaFileAlt className="lead text-muted" aria-hidden={true} /> = section has enrollment restrictions</p>
           <p><a href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/faqs/" target="_blank">Why does a course or section have enrollment restrictions?</a></p>
           <p><a href="http://www.washington.edu/students/reg/depttools/tsupdate/dl.html" target="_blank">Learn more about Distance Learning types</a></p>
       </HelpPopover>;

    let terms = props.terms.map(function (termData, i) {
        let curriculumComments = null;
        if (termData.curriculumComments) {
            const CurriculumCommentsToggle = ({children, eventKey}) => {
                const decoratedOnClick = useAccordionButton(eventKey, () => {});

                return (
                    <Button variant="accordion"
                            title={children}
                            onClick={decoratedOnClick}>
                        {children}
                    </Button>
                )

            };
            curriculumComments =
                <Accordion className="mb-3">
                    <CurriculumCommentsToggle eventKey={termData.yearTerm.term}>
                        View a note about {props.course.subjectArea} curriculum
                    </CurriculumCommentsToggle>
                    <Accordion.Collapse eventKey={termData.yearTerm.term}>
                        <>{termData.curriculumComments}</>
                    </Accordion.Collapse>
                </Accordion>;
        }

        return (
            <Card border="transparent" key={i}>
                <Card.Header className={classnames('d-flex', 'align-items-center', 'bg-' + TermUtils.TERMS[termData.yearTerm.term].toLowerCase())}>
                    <span className="flex-grow-1 text-uppercase fs-4">{termData.term}</span>
                    <span>{help}</span>
                </Card.Header>
                <Card.Body className="px-0">
                    {curriculumComments}
                    <SectionsTable course={props.course} sections={termData.activityOfferingItemList} termName={termData.term} {...props}/>
                </Card.Body>
            </Card>
        );
    });

    return (
        <>{terms}</>
    );
};


SectionsTermPanel.propTypes = {
   course: PropTypes.object.isRequired,
   terms: PropTypes.array.isRequired
};


/**
* The outer container for the sections part of the page.
*/

const SectionsPanel = (props) => {

   let component = null;

   if (props.institutions.length) {
       let institutionsTabs = [], institutionsPanes = [];

       props.institutions.map((institution, i) => {
           let pceInfo = (institution.code === InstitutionCodes.PCE ?
               <p>
                   For more information about PCE course registration, visit <a href="https://www.degreereg.uw.edu/how-to-register/" target="_blank">How-to-register site</a>.
               </p>
               : null);
           let institutionName = (institution.code === InstitutionCodes.PCE
               ? "Professional & Continuing Education (PCE)"
               : institution.name);
           institutionsPanes.push(
               <Tab.Pane key={i} eventKey={institution.code}>
                   {pceInfo}
                   <SectionsTermPanel course={props.course} terms={institution.courseOfferingTermList} {...props}/>
               </Tab.Pane>
           );

           institutionsTabs.push(
               <Nav.Item key={i}>
                   <Nav.Link eventKey={institution.code}>{institutionName}</Nav.Link>
               </Nav.Item>
           )
       });

       let appliedFiltersRow = "";
       if (props.filtersApplied) {
           let removeUrl = "/course/#/courses/" + props.course.code + "?id=" + props.course.courseId;
           appliedFiltersRow =
               <Col xs={12}>
                   <Alert variant="search" className="mt-3">
                       <dl className="row g-0 d-flex">
                           <dt className="col-6 col-sm-auto me-sm-auto order-1">Applied Filters:&nbsp;&nbsp;</dt>
                           <dd className="col flex-grow-1 mt-1 mt-sm-0 pe-sm-5 order-3 order-sm-2">{props.filtersApplied}</dd>
                           <dd className="col-6 col-sm-auto mt-0 order-2 order-sm-3 text-end"><a href={removeUrl}>Remove Filters</a></dd>
                       </dl>
                   </Alert>
               </Col>;
       }

       // Determine which tab to default to (PCE situation)
       let defaultActiveKey = props.institutions.map(i => i.code)[0]; // Default to the first institution code
       // Determine if there are section filters for CDP display
       let location = useLocation();
       let query = parse(location.search, { ignoreQueryPrefix: true });
       if ( query.hasOwnProperty("sectionFilters") ) {
           // Determine if the section filters has PCE only option set to true
           let parsedSectionFilters = parseJson(query.sectionFilters);
           let pceOnly = null;
           if ( parsedSectionFilters ){
               pceOnly = parsedSectionFilters.pceOnly;
           }
           if ( pceOnly ) {
               // Set default active key to the institution code for PCE (which is 95)
               defaultActiveKey = InstitutionCodes.PCE;
           }
       }

       component =
           <Row>
               <Col xs={12}>
                   <h2>{props.course.code} Course Sections</h2>
               </Col>
               {appliedFiltersRow}
               <Col xs={12}>
                   <Tab.Container id="course-institutions" defaultActiveKey={defaultActiveKey}>
                       <Nav variant="tabs" className="mt-3">
                           {institutionsTabs}
                       </Nav>
                       <Tab.Content className="p-3 pb-0 border border-top-0">
                           {institutionsPanes}
                       </Tab.Content>
                   </Tab.Container>
               </Col>
           </Row>;
   }

   return component;
};

SectionsPanel.propTypes = {
   course: PropTypes.object.isRequired,
   institutions: PropTypes.array.isRequired,
   filtersApplied: PropTypes.string
};

export default SectionsPanel;